// src/layouts/AdminLayout.tsx
import React, { useState } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  BookOpen,
  FolderTree,
  Video,
  Users,
  DollarSign,
  Mail,
  Settings,
  LogOut,
  Menu,
  X,
  TrendingUp,
  BarChart3,
} from "lucide-react";
import { clearCredentials } from "../slices/authSlice";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { useLogoutMutation } from "../services/auth";

const AdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [logoutApi] = useLogoutMutation();
  const auth = useAppSelector((s) => s.auth);

  async function handleLogout() {
    try {
      await logoutApi(undefined).unwrap();
    } catch (err) {
      // ignore
    } finally {
      dispatch(clearCredentials());
      navigate("/login");
    }
  }

  const navItems = [
    {
      title: "Dashboard",
      icon: LayoutDashboard,
      path: "/admin",
      description: "Overview & analytics",
    },
    {
      title: "Courses",
      icon: BookOpen,
      path: "/admin/courses",
      description: "Manage courses",
    },
    {
      title: "Modules",
      icon: FolderTree,
      path: "/admin/modules",
      description: "Course modules",
    },
    {
      title: "Lessons",
      icon: Video,
      path: "/admin/lessons",
      description: "Video lessons",
    },
    {
      title: "Students",
      icon: Users,
      path: "/admin/students",
      description: "User management",
    },
    {
      title: "Revenue",
      icon: DollarSign,
      path: "/admin/revenue",
      description: "Payments & earnings",
    },
    {
      title: "Outstanding",
      icon: TrendingUp,
      path: "/admin/outstanding",
      description: "Pending payments",
    },
    {
      title: "Email",
      icon: Mail,
      path: "/admin/email",
      description: "Send emails",
    },
    {
      title: "Reports",
      icon: BarChart3,
      path: "/admin/reports",
      description: "Analytics & stats",
    },
  ];

  const isActive = (path: string) => {
    if (path === "/admin") {
      return location.pathname === "/admin";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? "w-72" : "w-0"
        } bg-gradient-to-b from-[#0f1724] to-[#1a2332] text-white flex-shrink-0 transition-all duration-300 overflow-hidden`}
      >
        <div className="h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="p-6 border-b border-white/10">
            <Link to="/admin" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center font-bold text-lg">
                A
              </div>
              <div>
                <div className="font-semibold text-lg">Admin Panel</div>
                <div className="text-xs text-gray-400">Digital Income Blueprint</div>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4">
            <div className="space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      active
                        ? "bg-purple-500/20 text-purple-300 border border-purple-500/30"
                        : "hover:bg-white/5 text-gray-300"
                    }`}
                  >
                    <Icon size={20} className="flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{item.title}</div>
                      <div className="text-xs text-gray-400 truncate">
                        {item.description}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-3 px-2">
              <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center">
                <span className="font-semibold text-sm">
                  {auth.firstName?.[0] || auth.email?.[0] || "A"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate text-sm">{auth.email}</div>
                <div className="text-xs text-gray-400">Administrator</div>
              </div>
            </div>

            <div className="space-y-1">
              <Link
                to="/admin/settings"
                className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-white/5 transition-colors text-sm text-gray-300"
              >
                <Settings size={16} />
                <span>Settings</span>
              </Link>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-white/5 transition-colors text-sm text-gray-300"
              >
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {navItems.find((item) => isActive(item.path))?.title || "Admin Dashboard"}
              </h1>
              <p className="text-sm text-gray-500">
                {navItems.find((item) => isActive(item.path))?.description || "Manage your platform"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              View Site
            </Link>
            <Link
              to="/dashboard"
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Student View
            </Link>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;